import React from 'react';

const ResearchFocusAreas = () => {
  // Grouping styles into a single object for organization
  const styles = {
    container: {
      minHeight: '100vh',
      background: '#0a0a0a',
      padding: '4rem 2rem',
      fontFamily: 'Arial, sans-serif'
    },
    content: {
      maxWidth: '1200px',
      margin: '0 auto'
    },
    header: {
      textAlign: 'center',
      marginBottom: '4rem'
    },
    title: {
      fontSize: '3rem',
      fontWeight: 'bold',
      color: 'white',
      margin: '0 0 1.5rem 0'
    },
    subtitle: {
      fontSize: '1.25rem',
      color: '#9ca3af',
      lineHeight: '1.6',
      maxWidth: '800px',
      margin: '0 auto'
    },
    grid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(500px, 1fr))',
      gap: '2rem',
    },
    card: {
      background: 'rgba(20, 20, 20, 0.8)',
      border: '1px solid #2d2d2d',
      borderRadius: '16px',
      padding: '2rem',
      transition: 'all 0.3s ease',
      cursor: 'pointer',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
    },
    cardHeader: {
      display: 'flex',
      alignItems: 'center',
      gap: '1rem',
      marginBottom: '1rem'
    },
    iconContainer: (color) => ({
      width: '56px',
      height: '56px',
      borderRadius: '12px',
      background: color,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0
    }),
    iconSvg: {
      width: '28px',
      height: '28px',
      fill: 'white'
    },
    cardTitle: {
      fontSize: '1.5rem',
      fontWeight: 'bold',
      color: 'white',
      margin: 0
    },
    description: {
      color: '#9ca3af',
      lineHeight: '1.6',
      marginBottom: '2rem',
      fontSize: '1rem'
    },
    footer: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    },
    projectCount: {
      color: '#6b7280',
      fontSize: '0.9rem'
    },
    learnMore: {
      color: 'white',
      textDecoration: 'none',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      fontSize: '0.9rem',
      fontWeight: '500',
      transition: 'color 0.3s ease'
    },
    arrow: {
      width: '16px',
      height: '16px',
      fill: 'currentColor'
    }
  };

  const researchAreas = [
    { id: 'ml', title: 'Personalized Conversational AI', description: 'Advancing user-adaptive language models for tailored interactions and contextual understanding', projects: '12 active projects', iconColor: 'linear-gradient(135deg, #6366f1, #8b5cf6)', icon: <svg style={styles.iconSvg} viewBox="0 0 24 24"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/></svg> },
    { id: 'ethics', title: 'AI Ethics & Fairness', description: 'Ensuring responsible AI development and deployment across global communities', projects: '8 active projects', iconColor: 'linear-gradient(135deg, #10b981, #059669)', icon: <svg style={styles.iconSvg} viewBox="0 0 24 24"><path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4z"/></svg> },
    { id: 'education', title: 'Educational AI Systems', description: 'Creating adaptive learning platforms for personalized education experiences', projects: '15 active projects', iconColor: 'linear-gradient(135deg, #f59e0b, #d97706)', icon: <svg style={styles.iconSvg} viewBox="0 0 24 24"><path d="M12 3L1 9l11 6 9-4.91V17h2V9L12 3z"/></svg> },
    { id: 'social', title: 'Multilingual AI Platforms', description: 'Building inclusive AI systems that understand and generate content across diverse languages and dialects', projects: '10 active projects', iconColor: 'linear-gradient(135deg, #8b5cf6, #7c3aed)', icon: <svg style={styles.iconSvg} viewBox="0 0 24 24"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/></svg> }
  ];

  return (
    <div style={styles.container} data-component="container">
      <style>
        {`
          /* CSS Hover Effects */
          [data-component="card"]:hover {
            transform: translateY(-4px) !important;
            box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3) !important;
          }
          [data-component="learn-more"]:hover {
            color: #a78bfa !important; /* Lighter purple on hover */
          }

          /* Responsive Styles */
          @media (max-width: 1024px) {
            /* On tablets, auto-fit will likely handle stacking, 
               but we can be explicit if needed */
            [data-component="grid"] {
              grid-template-columns: 1fr !important;
            }
          }

          @media (max-width: 768px) {
            [data-component="container"] {
              padding: 3rem 1rem !important;
            }
            [data-component="title"] {
              font-size: 2.5rem !important;
            }
            [data-component="subtitle"] {
              font-size: 1.1rem !important;
            }
            [data-component="card"] {
              width: 95% !important;
              margin: 0 auto !important;
            }
          }
        `}
      </style>
      <div style={styles.content}>
        <div style={styles.header}>
          <h2 style={styles.title} data-component="title">
            Research Focus Areas
          </h2>
          <p style={styles.subtitle} data-component="subtitle">
            Our multidisciplinary research spans across critical areas of AI development,
            education, and social impact.
          </p>
        </div>

        <div style={styles.grid} data-component="grid">
          {researchAreas.map((area) => (
            <div key={area.id} style={styles.card} data-component="card">
              <div style={styles.cardHeader}>
                <div style={styles.iconContainer(area.iconColor)}>
                  {area.icon}
                </div>
                <h3 style={styles.cardTitle}>{area.title}</h3>
              </div>
              
              <p style={styles.description}>{area.description}</p>
              
              <div style={styles.footer}>
                <span style={styles.projectCount}>{area.projects}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default ResearchFocusAreas;